import { useRef, useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import LocationPicker from "@/components/LocationPicker";
import { Loader2, Camera, Upload, Send, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import CameraCapture from "@/components/CameraCapture";

const inputCls = "w-full h-10 px-3 rounded-md border border-[#E5E7EB] bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent text-sm";

export default function FieldReport() {
  const [description, setDescription] = useState("");
  const [wilayah, setWilayah] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const onCameraShot = async (blob, filename) => {
    const f = new File([blob], filename, { type: "image/jpeg" });
    pickFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Foto temuan wajib diunggah"); return; }
    if (!description.trim()) { toast.error("Deskripsi wajib diisi"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ description });
      if (wilayah) params.append("wilayah", wilayah);
      if (typeof lat === "number") params.append("lat", String(lat));
      if (typeof lng === "number") params.append("lng", String(lng));
      await api.post(`/field-reports?${params}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Laporan terkirim — menunggu persetujuan koordinator");
      setDescription(""); setWilayah(""); setLat(null); setLng(null);
      setFile(null); setPreview(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="field-report-page">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Teknisi Lapangan</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <ClipboardList className="h-7 w-7 text-[#EA580C]" /> Laporkan Temuan
        </h1>
        <p className="text-sm text-[#4B5563] mt-2">
          Kirim temuan lapangan dengan cepat. Laporan akan diverifikasi koordinator sebelum menjadi tiket resmi.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5 bg-white border border-[#E5E7EB] rounded-md p-6">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-2">Foto Temuan *</label>
          {preview ? (
            <div className="relative w-full max-w-md">
              <img src={preview} alt="preview" className="w-full h-64 object-cover rounded-md border border-[#E5E7EB]" data-testid="fr-preview" />
              <button type="button" onClick={() => { setFile(null); setPreview(null); }} className="absolute top-2 right-2 h-8 px-2 rounded-md bg-white/95 text-[#991B1B] text-xs font-semibold shadow-sm">Ganti</button>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => pickFile(e.target.files?.[0])} data-testid="fr-file" />
              <button type="button" onClick={() => inputRef.current?.click()} className="h-11 px-4 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8F9FA] text-sm font-semibold flex items-center gap-2" data-testid="fr-upload-btn">
                <Upload className="h-4 w-4" /> Pilih Foto
              </button>
              <button type="button" onClick={() => setCameraOpen(true)} className="h-11 px-4 rounded-md bg-[#0A0A0A] hover:bg-[#333] text-white text-sm font-semibold flex items-center gap-2" data-testid="fr-camera-btn">
                <Camera className="h-4 w-4" /> Kamera
              </button>
            </div>
          )}
        </div>

        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-2">Wilayah / Lokasi *</label>
          <LocationPicker
            value={wilayah}
            lat={lat}
            lng={lng}
            onChange={({ lat: l1, lng: l2, wilayah: w }) => {
              setLat(l1); setLng(l2);
              if (w !== undefined) setWilayah(w || wilayah);
            }}
          />
          {wilayah && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Wilayah terdeteksi:</span>
              <span className="inline-flex items-center gap-1 bg-[#0055FF]/10 text-[#0044CC] text-xs font-bold px-2 py-1 rounded-md" data-testid="fr-wilayah-badge">{wilayah}</span>
            </div>
          )}
        </div>

        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-[#4B5563] mb-2">Deskripsi Temuan *</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className={`${inputCls} h-auto py-2`}
            placeholder="Contoh: Kabel FO putus tertimpa pohon di Km 3 setelah jembatan..."
            data-testid="fr-desc"
          />
        </div>

        <div className="pt-2 border-t border-[#E5E7EB] flex justify-end">
          <button type="submit" disabled={saving} className="h-11 px-6 rounded-md bg-[#EA580C] hover:bg-[#C2410C] disabled:opacity-60 text-white font-semibold flex items-center gap-2" data-testid="fr-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Kirim Laporan
          </button>
        </div>
      </form>

      <CameraCapture open={cameraOpen} onOpenChange={setCameraOpen} onCapture={onCameraShot} kindLabel="Temuan" />
    </div>
  );
}
